import { ArrowUpRightOnBox, Bookmarks, Trash } from "@medusajs/icons"
import { Button, Skeleton, Text, toast, usePrompt } from "@medusajs/ui"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { Link } from "react-router-dom"
import { useCosts, withChinaShipping } from "../lib/cost-settings"
import { finderApi, type FinderPickRow } from "../lib/finder-api"
import { ago, formatNumber } from "../lib/format"
import { caps, t } from "../lib/i18n"
import { errorMessage } from "../lib/medusa"
import { AddToOrder, BottomSheet, pickFound } from "./add-to-order"
import { Empty } from "./ui"

export const usePicks = () => useQuery({ queryKey: ["hq", "finder", "picks"], queryFn: finderApi.picks, staleTime: 30_000 })

const where = (p: FinderPickRow) => (p.album_id ? `/finder/a/${encodeURIComponent(p.album_id)}` : `/finder/link?url=${encodeURIComponent(p.link)}`)

function PickCard({ pick, children, corner }: { pick: FinderPickRow; children?: React.ReactNode; corner?: React.ReactNode }) {
  return (
    <div className="bg-ui-bg-base shadow-elevation-card-rest relative flex gap-x-3 rounded-lg p-2.5">
      {corner && <div className="absolute right-1 top-1 flex">{corner}</div>}
      <Link to={where(pick)} className="shrink-0">
        {pick.image ? <img src={pick.image} alt="" loading="lazy" className="bg-ui-bg-subtle size-20 rounded-md object-cover" /> : <span className="bg-ui-bg-subtle block size-20 rounded-md" />}
      </Link>
      <div className={`flex min-w-0 flex-1 flex-col gap-y-1 ${corner ? "pr-16" : ""}`}>
        <Link to={where(pick)} className="txt-compact-small-plus line-clamp-2 break-words">{pick.en || pick.title}</Link>
        {pick.zh && <span className="hc-data text-ui-fg-muted truncate text-[11.5px]">{pick.zh}</span>}
        <span className="txt-compact-xsmall text-ui-fg-muted">
          {[pick.yuan != null ? `¥${formatNumber(pick.yuan)}` : null, ago(pick.created_at)].filter(Boolean).join(" · ")}
        </span>
        {children}
      </div>
    </div>
  )
}

/**
 * HQ → Supplier finder → Saved: the products kept for later. Each one leaves the list once a custom
 * order with it is saved; «Clear the list» empties it for the next time.
 */
export function SavedPicksList() {
  const queryClient = useQueryClient()
  const prompt = usePrompt()
  const picks = usePicks()
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["hq", "finder", "picks"] })
  const remove = useMutation({ mutationFn: finderApi.removePick, onSuccess: refresh, onError: (error) => toast.error(errorMessage(error)) })
  const clear = useMutation({
    mutationFn: finderApi.clearPicks,
    onSuccess: (answer) => {
      refresh()
      toast.success(t.n(answer.removed, "{n} item cleared", "{n} items cleared"))
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const list = picks.data?.picks ?? []
  const used = picks.data?.used ?? []
  const askClear = async () => {
    const ok = await prompt({ title: t("Clear the saved list?"), description: t("Every item on it is taken off. Orders are not changed."), confirmText: t("Clear"), cancelText: t("Cancel") })
    if (ok) clear.mutate()
  }
  return (
    <div className="flex flex-col gap-y-3">
      <div className="flex items-center justify-between gap-x-3">
        <Text size="small" className="text-ui-fg-subtle">
          {t("Products kept for later. Each one leaves the list by itself once a custom order with it is saved.")}
        </Text>
        {list.length > 0 && (
          <Button size="small" variant="secondary" className="shrink-0" isLoading={clear.isPending} onClick={() => void askClear()}>
            {t("Clear the list")}
          </Button>
        )}
      </div>
      {picks.isLoading && <Skeleton className="h-40 w-full rounded-lg" />}
      {picks.isError && <Text className="text-ui-fg-error">{errorMessage(picks.error)}</Text>}
      {picks.data && !list.length && <Empty title={t("Nothing saved")}>{t("In the finder, «Add to an order» → «Save for later» keeps a product here.")}</Empty>}
      {list.map((pick) => (
        <PickCard
          key={pick.id}
          pick={pick}
          corner={
            <>
              <Button size="small" variant="transparent" asChild>
                <a href={pick.link} target="_blank" rel="noreferrer" aria-label={t("Open the supplier's page")}>
                  <ArrowUpRightOnBox />
                </a>
              </Button>
              <Button size="small" variant="transparent" aria-label={t("Take it off the list")} isLoading={remove.isPending && remove.variables === pick.id} onClick={() => remove.mutate(pick.id)}>
                <Trash />
              </Button>
            </>
          }
        >
          <AddToOrder found={pickFound(pick)} className="mt-1 self-start" />
        </PickCard>
      ))}
      {used.length > 0 && (
        <div className="flex flex-col gap-y-2 pt-2">
          <p className="hc-eyebrow">{caps(t("Used this week"))}</p>
          {used.map((pick) => (
            <PickCard key={pick.id} pick={pick}>
              {pick.used_order_number && (
                <Link to={`/custom/${encodeURIComponent(pick.used_order_number)}`} className="txt-compact-xsmall-plus text-ui-fg-interactive self-start">
                  {t("In order {ref}", { ref: `C-${pick.used_order_number}` })}
                </Link>
              )}
            </PickCard>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * «From saved» inside the order form: a saved product becomes an item (its link, ¥ with shipping in
 * China, English and the seller's Chinese), and leaves the list when the order is saved.
 */
export function SavedPicker({ onPick }: { onPick: (pick: FinderPickRow, costYuan: number | null) => void }) {
  const [open, setOpen] = useState(false)
  const picks = usePicks()
  const costs = useCosts()
  const list = picks.data?.picks ?? []
  if (!list.length) return null
  return (
    <>
      <Button type="button" variant="secondary" size="small" onClick={() => setOpen(true)}>
        <Bookmarks /> {t("From saved ({n})", { n: list.length })}
      </Button>
      {open && (
        <BottomSheet title={t("Your saved items")} onClose={() => setOpen(false)}>
          <div className="flex flex-col gap-y-2">
            {list.map((pick) => (
              <button
                key={pick.id}
                type="button"
                onClick={() => {
                  onPick(pick, pick.yuan != null ? withChinaShipping(pick.yuan, costs) : null)
                  setOpen(false)
                }}
                className="hover:bg-ui-bg-base-hover flex items-center gap-x-3 rounded-lg p-1.5 text-left"
              >
                {pick.image ? <img src={pick.image} alt="" loading="lazy" className="bg-ui-bg-subtle size-14 shrink-0 rounded-md object-cover" /> : <span className="bg-ui-bg-subtle size-14 shrink-0 rounded-md" />}
                <span className="flex min-w-0 flex-col">
                  <span className="txt-compact-small-plus line-clamp-2 break-words">{pick.en || pick.title}</span>
                  {pick.zh && <span className="hc-data text-ui-fg-muted truncate text-[11.5px]">{pick.zh}</span>}
                  {pick.yuan != null && <span className="hc-data text-ui-fg-subtle text-[12px]">¥{formatNumber(pick.yuan)}</span>}
                </span>
              </button>
            ))}
          </div>
        </BottomSheet>
      )}
    </>
  )
}
